
import { defineComponent, PropType } from "vue";
import { Insight } from "@/models/insight";
import { toggleHeatmap } from "@/modules/heatmaps/heatmaps-utils";
import { Observation } from "@/store/reports/models";
import { Likelihood } from "@/models/labels";
import { EventBus } from "@/eventBus";
import { mapGetters, mapState } from "vuex";
import { reportsGetters } from "@/store/reports/types";
import { Button } from "@progress/kendo-vue-buttons";
import { RootGetters } from "@/store/getters";
import { useI18n } from "vue-i18n";
import ObservationController from "@/modules/reports/controllers/ObservationController.vue";
import IbIcons from "@/components/icons/ib-icons.vue";
import AiFinding from "./AiFinding.vue";

export default defineComponent({
  name: "Insight",
  data() {
    return {
      copyFindingValue: null,
    };
  },
  components: {
    ObservationController,
    "k-button": Button,
    IbIcons,
    AiFinding,
  },
  setup() {
    const { t } = useI18n({
      inheritLocale: true,
      useScope: "local",
    });
    return { t };
  },
  props: {
    insight: {
      type: Object as PropType<Insight>,
      required: true,
    },
    isActive: {
      type: Boolean,
    },
    prefill: {
      type: [String, Number, Array],
    },
    isSlideReportComplete: {
      type: Boolean,
      default: false,
    },
    isAnalysisPerformed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(["activeCaseId", "activeSlideId", "insightsConfig", "currentSlide"]),
    ...mapGetters({
      config: RootGetters.CONFIG,
      activeCase: RootGetters.GET_ACTIVE_CASE,
      activeSlide: RootGetters.ACTIVE_SLIDE,
      pois: RootGetters.SLIDE_POIS,
    }),
    ...mapGetters("reportsModule", {
      caseReport: reportsGetters.ACTIVE_CASE_REPORT,
      getObservation: reportsGetters.OBSERVATION,
    }),
    getLegendIcon() {
      let style = this.insight.icon.iconStyle;
      return style;
    },
    insightMessage() {
      return this.t(`${this.insight.message}`);
    },
    poi() {
      return this.pois.filter((poi) => poi["POI Type"] === `screening_${this.insight.key}`)[0];
    },
    computedPrefill() {
      if (this.copyFindingValue) {
        return this.copyFindingValue;
      }
      return this.prefill;
    },
    isHidden() {
      // make sure not to trigger the logic when moving from viewer to the case/s list
      if (!this.activeSlide) return false;

      // don't hide if there's no show/hide configuration for this insight
      if (!this.insight.controller.target_observation) {
        return false;
      } else {
        let watchedObservation = this.getObservation(
          this.activeCaseId,
          this.activeSlideId,
          this.insight.controller.target_observation
        );
        const targetObservationConfigs =
          this.insightsConfig.slide_insights[this.activeCase.tissue][this.activeSlide.stain];
        if (!targetObservationConfigs) return false;

        const targetObservationConfig = targetObservationConfigs.find(
          (config) => config.key === watchedObservation.key
        );

        // we have to test the translated strings and not the enums...
        let translatedTargetValues = [];
        this.insight.controller.watch_target_values.map((value) => {
          let translateValue;
          if (targetObservationConfig?.controller.type === "input") {
            //no need to translate
            translateValue = value;
          } else if (targetObservationConfig?.controller.type === "observation_single") {
            translateValue = this.t(`config.insight.observation.singleSelect.${value}`);
          } else {
            translateValue = this.t(
              `config.insight.slide.${this.activeCase.tissue}.${this.activeSlide.stain}.${watchedObservation.key}.controller.${value}`
            );
          }
          translatedTargetValues.push(translateValue);
        });
        let isRuleActive;
        if (watchedObservation.value instanceof Array) {
          isRuleActive = translatedTargetValues.some((r) => watchedObservation.value.includes(r));
        } else {
          isRuleActive = translatedTargetValues.includes(watchedObservation.value);
        }
        if (this.insight.controller.watch_rule === "show") {
          return isRuleActive ? false : true;
        } else {
          return isRuleActive ? true : false;
        }
      }
    },
  },
  methods: {
    toggleHeatmap,
    setActive(insight: Insight) {
      if (insight?.suffix === "mm" && !this.isSlideReportComplete) {
        EventBus.emit("event-bridge-api", {
          eventName: "set-measuring-context",
          data: { key: insight.key },
        });
      } else {
        EventBus.emit("event-bridge-api", {
          eventName: "set-measuring-context",
          data: { key: null },
        });
      }
      const associated_heatmap =
        insight.likelihood === Likelihood.LOW.toLowerCase()
          ? `screening_${insight.key}`.toLowerCase()
          : insight.associated_heatmap?.toLowerCase();
      this.toggleHeatmap(associated_heatmap, false);
      this.$emit("setActive", insight);
    },
    handleChange(observation: Observation) {
      if (this.isHidden) observation.value = "";
      observation.title = this.t(
        `config.insight.slide.${this.activeCase.tissue}.${this.activeSlide.stain}.${this.insight.key}.title`
      );
      this.$emit("observationChange", observation);
      this.copyFindingValue = observation.value;
    },
    copyAIFindings() {
      // if slide report is confirmed, do not change state
      if (this.isSlideReportComplete) return;
      // switch between different input types
      const observation: Observation = {
        key: this.insight.key,
        value: "",
        title: this.t(
          `config.insight.slide.${this.currentSlide.tissueType}.${this.currentSlide.stain}.${this.insight.key}.title`
        ),
      };
      switch (this.insight.controller.type) {
        case "input":
          let fixedValue;
          if (this.insight.value !== "undefined" && this.insight.value !== null) {
            fixedValue = (this.insight.suffix==="%")? this.insight.value.toFixed(2):this.insight.value.toFixed(1);
          } else {
            fixedValue = this.insight.value;
          }
          if (this.insight.suffix && this.insight.controller.inputType !== "number") {
            fixedValue += this.insight.suffix;
            console.log(fixedValue);
          }
          if (this.insight.key === "her2_count_total_invasive") {
            fixedValue = this.insight.value.toFixed(0);
          }
          this.copyFindingValue = fixedValue;
          observation.value = fixedValue;
          break;
        case "select":
        default:
          this.copyFindingValue = this.insight.value;
          observation.value = this.insight.value;
          break;
      }
      this.handleChange(observation);
    },
  },
  mounted() {
    if (this.isActive) {
      this.setActive(this.insight);
      this.$el.focus();
    }
  },
  watch: {
    isHidden(newVal) {
      if (newVal === true) {
        this.insight.prefill = "";
        this.handleChange({ key: this.insight.key, value: "", title: "" });
      }
    },
  },
});
